// "Install EduMIS" popup — shown once per session after a user signs in on the
// web, inviting them to install the PWA. It:
//   • never shows when already running installed (PWA/standalone/iOS home-screen)
//   • auto-disappears after 30s, and can be dismissed by the user
//   • on Chromium fires the native install prompt; on iOS shows Share instructions
// Mounted once globally (in FinanceShell) so it can react to login on any route.
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Snackbar, Paper, Box, Typography, Button, IconButton } from '@mui/material';
import { Close, InstallMobile, IosShare, AddBoxOutlined } from '@mui/icons-material';
import {
  isStandalone, isIOS, getDeferredPrompt, clearDeferredPrompt, onInstallChange,
} from '../utils/pwaInstall';

const AUTO_HIDE_MS   = 30000;              // auto-disappear after 30 seconds
const COOLDOWN_DAYS  = 14;                 // after a dismiss, stay quiet this long
const SESSION_KEY    = 'edumis_install_prompt_shown';
const COOLDOWN_KEY   = 'edumis_install_dismissed_until';

const lsGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };
const ssGet = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const ssSet = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } };

export default function InstallPrompt() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0); // bumps when install availability changes

  // Re-evaluate if the deferred prompt arrives (or the app gets installed) after
  // this component has already mounted.
  useEffect(() => onInstallChange(() => setTick(t => t + 1)), []);

  // Decide whether to show — once per session, gated by install state + cooldown.
  useEffect(() => {
    if (open) return;
    if (isStandalone()) return;                 // already installed — never nag
    if (!lsGet('token')) return;                // not signed in (login/landing)
    if (ssGet(SESSION_KEY)) return;             // already shown this session
    const until = Number(lsGet(COOLDOWN_KEY)) || 0;
    if (Date.now() < until) return;             // dismissed recently
    const installable = !!getDeferredPrompt() || isIOS();
    if (!installable) return;                   // nothing to offer (yet)

    // Small delay so it lands just after the dashboard paints, not mid-navigation.
    const t = setTimeout(() => { setOpen(true); ssSet(SESSION_KEY, '1'); }, 1500);
    return () => clearTimeout(t);
  }, [location.pathname, tick, open]);

  // Auto-disappear after 30s.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setOpen(false), AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [open]);

  const dismiss = useCallback(() => {
    setOpen(false);
    lsSet(COOLDOWN_KEY, String(Date.now() + COOLDOWN_DAYS * 86400000));
  }, []);

  const install = useCallback(async () => {
    const dp = getDeferredPrompt();
    if (!dp) { setOpen(false); return; }
    setOpen(false);
    try { dp.prompt(); await dp.userChoice; } catch { /* user cancelled */ }
    clearDeferredPrompt();
  }, []);

  if (!open) return null;
  const ios = isIOS() && !getDeferredPrompt();

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: { xs: 9, sm: 3 }, px: { xs: 1, sm: 0 } }} // clear the mobile menu handle / FAB
    >
      <Paper elevation={8}
        sx={{ p: 1.5, borderRadius: 2, maxWidth: 420, width: '100%',
          display: 'flex', alignItems: 'flex-start', gap: 1.5,
          border: '1px solid', borderColor: 'divider' }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/masomis.png`}
          alt="EduMIS"
          sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            Install EduMIS
          </Typography>
          {ios ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Tap <IosShare sx={{ fontSize: 14, verticalAlign: 'text-bottom' }} /> Share, then{' '}
              <AddBoxOutlined sx={{ fontSize: 14, verticalAlign: 'text-bottom' }} /> <strong>Add to Home Screen</strong>.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                Add it to your device for quick, full-screen access.
              </Typography>
              <Button
                size="small" variant="contained" startIcon={<InstallMobile />}
                onClick={install}
                sx={{ mt: 1, textTransform: 'none' }}>
                Install app
              </Button>
            </>
          )}
        </Box>
        <IconButton size="small" onClick={dismiss} aria-label="Dismiss" sx={{ mt: -0.5, mr: -0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
}
